import { useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import * as yup from 'yup';

import useFaqSubmit from '@/components/atoms/FileUpload/useFaqSubmit';
import MarkdownEditorBlockNote from '@/components/atoms/MarkdownEditor/MarkdownEditorBlockNote';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  FormLabel,
  FormDescription,
} from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import useDatasources from '@/hooks/useDatasources';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useForceRerender } from '@/hooks/useForceRerender';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { useDocumentContent } from '@/modules/collections/hooks/useDocumentContent';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectAccountId, selectDatasourceId, selectDocumentId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { DatasourceType } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { FILES_BASE_URL } from '@/util/constants';
import { hasDirtyFields, useHasDirtyFields } from '@/util/util';

import {
  MAX_TXT_PREVIEW_LENGTH,
  ROOT_DATASOURCE_NAMES,
  ROOT_DATASOURCE_TYPES,
} from '../../constants';
import { useAPIDatasourcesDocuments } from '../../hooks/useAPIDatasourcesDocuments';
import { useAPIDocuments } from '../../hooks/useAPIDocuments';
import { useTabCategory } from '../../hooks/useCategory';
import { computeTxtPreview } from '../../utils/preview';
import { addFAQSchema, checkDocumentNameAvailability } from '../../validator';

type FormValues = yup.InferType<ReturnType<typeof addFAQSchema>>;

export const AddFAQ = () => {
  // Redux selectors
  const documentId = useSelector(selectDocumentId);
  const accountId = useSelector(selectAccountId);
  const datasourceIdFromRedux = useSelector(selectDatasourceId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const { editorKey, forceRerender } = useForceRerender();

  // Hooks
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { collectionId, datasourceId: datasourceIdParam } = useParams();
  const datasourceId = datasourceIdParam || datasourceIdFromRedux;

  const {
    updateDocumentAsync,
    createDocumentAsync,
    getStatus: getStatusFromInsideFolder,
    getDocumentById,
  } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: datasourceId,
    document_id: documentId,
  });

  const { category } = useTabCategory();
  const { getDatasourceDocumentById, getStatus: getStatusFromOutsideFolder } =
    useAPIDatasourcesDocuments({ collection_id: collectionId, datasourceTab: category });
  const documentFromOutsideFolder = getDatasourceDocumentById(documentId);
  const documentFromInsideFolder = getDocumentById(documentId);
  const document = documentFromInsideFolder || documentFromOutsideFolder;
  const getStatus = documentFromOutsideFolder
    ? getStatusFromOutsideFolder
    : getStatusFromInsideFolder;

  // Use the new custom hook for document content
  const { content: documentContent } = useDocumentContent({
    collectionId,
    datasourceId,
    documentId,
    enabled: !!datasourceId && !!documentId && !!collectionId,
  });

  const { uploadFileToUrl } = useFaqSubmit();
  const { datasources, createDatasourceAsync } = useDatasources(collectionId, datasourceId);

  // Local state
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  // Local variables
  const isEditing = !!datasourceId && !!documentId;
  const isFormLoading = isEditing && getStatus === 'pending';

  const showKeepPublished = isEditing && !document?.is_draft && document?.status === 'available';

  // RHF
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(
      addFAQSchema(
        isEditing,
        collectionId || '',
        datasourceId || '',
        documentId || '',
        document?.name
      )
    ),
    defaultValues: {
      question: document?.name || '',
      answer: documentContent || '',
      ...(isEditing && { keep_published: !document?.is_draft }),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (document && documentContent) {
      form.resetField('question', { defaultValue: document.name, keepDirty: false });
      form.resetField('answer', { defaultValue: documentContent, keepDirty: false });
      forceRerender();
    }
  }, [document, documentContent, forceRerender, form]);

  const resetForm = () => {
    // Reset form to prepare for next FAQ creation
    form.reset({ question: '', answer: '' });
    forceRerender();
  };

  const resetReduxState = () => {
    dispatch(setDatasourceId(null));
    dispatch(setDocumentId(null));
  };

  const saveFAQToDatasource = async (datasourceId: string, data: FormValues): Promise<boolean> => {
    // Check for duplicate name before uploading
    const nameCheckResult = await checkDocumentNameAvailability({
      collectionId,
      datasourceId,
      name: data.question,
    });

    if (!nameCheckResult.available) {
      form.setError('question', {
        type: 'manual',
        message: nameCheckResult.message || 'This name is already taken',
      });
      return false;
    }

    const file = new File([data.answer], data.question, { type: 'text/plain' });
    let url: string;

    try {
      const resp = await uploadFileToUrl(
        file,
        `${FILES_BASE_URL}/${accountId}/${collectionId}/${datasourceId}`
      );
      url = resp?.url;

      await createDocumentAsync({
        datasource_id: datasourceId,
        type: 'txt',
        name: data.question,
        file_url: url,
        metadata: { preview: computeTxtPreview(data.answer, MAX_TXT_PREVIEW_LENGTH) },
      });
      return true;
    } catch (error) {
      console.error('Failed to upload file:', error);
      return false;
    }
  };

  const handleEditFAQ = async (datasourceId: string, data: FormValues): Promise<boolean> => {
    const originalName = document?.name;
    const newName = data.question.trim();
    const nameHasChanged = !originalName || newName.toLowerCase() !== originalName.toLowerCase();

    // Only check for duplicate name if the name has changed
    if (nameHasChanged) {
      const nameCheckResult = await checkDocumentNameAvailability({
        collectionId,
        datasourceId,
        name: data.question,
        documentId,
      });

      if (!nameCheckResult.available) {
        form.setError('question', {
          type: 'manual',
          message: nameCheckResult.message || 'This name is already taken',
        });
        return false;
      }
    }

    try {
      const file = new File([data.answer], data.question, { type: 'text/plain' });
      const resp = await uploadFileToUrl(
        file,
        `${FILES_BASE_URL}/${accountId}/${collectionId}/${datasourceId}`
      );

      await updateDocumentAsync({
        document_id: documentId,
        datasource_id: datasourceId,
        name: data.question,
        type: 'txt',
        file_url: resp?.url,
        is_draft: !data.keep_published,
        metadata: { preview: computeTxtPreview(data.answer, MAX_TXT_PREVIEW_LENGTH) },
      });
      return true;
    } catch (error) {
      console.error('Failed to upload file:', error);
      return false;
    }
  };

  // This function handles following cases:
  // 1. Add FAQ to root FAQ datasource when we are outside of a folder
  // 2. Add FAQ to the folder when we are inside a folder
  const onSubmit = async (data: FormValues) => {
    if (isEditing) {
      await handleEditFAQ(datasourceId, data);
      return;
    }

    const file = new File([data.answer], data.question, { type: 'text/plain' });
    const rootFAQDatasource = datasources?.find(
      (datasource) =>
        datasource.type === DatasourceType.FAQ && datasource.name === ROOT_DATASOURCE_NAMES.faq
    );
    const rootDatasourceExists = !!rootFAQDatasource;
    const isInsideFolder = !!datasourceIdParam;

    // Determine target datasource
    let targetDatasourceId: string;

    if (isInsideFolder) {
      targetDatasourceId = datasourceIdParam;
    } else if (rootDatasourceExists) {
      targetDatasourceId = rootFAQDatasource.datasource_id;
    } else {
      // Create new root datasource
      const { datasource_id } = await createDatasourceAsync(
        ROOT_DATASOURCE_TYPES[DatasourceType.FAQ]
      );
      targetDatasourceId = datasource_id;

      // Check for duplicate name before uploading
      const nameCheckResult = await checkDocumentNameAvailability({
        collectionId,
        datasourceId: targetDatasourceId,
        name: data.question,
      });

      if (!nameCheckResult.available) {
        form.setError('question', {
          type: 'manual',
          message: nameCheckResult.message || 'This name is already taken',
        });
        return;
      }

      // Upload file and create document
      try {
        const response = await uploadFileToUrl(
          file,
          `${FILES_BASE_URL}/${accountId}/${collectionId}/${targetDatasourceId}`
        );

        await createDocumentAsync({
          datasource_id: targetDatasourceId,
          type: 'txt',
          name: data.question,
          file_url: response?.url,
          metadata: { preview: computeTxtPreview(data.answer, MAX_TXT_PREVIEW_LENGTH) },
        });
      } catch (error) {
        console.error('Failed to upload file:', error);
      }

      resetForm();
      return;
    }

    // Save FAQ to the determined datasource
    const success = await saveFAQToDatasource(targetDatasourceId, data);

    if (success) {
      resetForm();
    }
  };

  const handleClose = () => {
    const hasUnsavedChanges = hasDirtyFields(form);

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      resetReduxState();
      dispatch(setSidebarContent(null));
    }
  };

  const isDirty = useHasDirtyFields(form.control);
  useTrackFormState({ isDirty, formId: 'add-faq' });

  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">
          {isEditing ? t('collections.add_faq.update_faq') : t('collections.add_faq.title')}
        </h2>

        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-foreground-muted" />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1 pb-1">
            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('collections.add_faq.question')}</FormLabel>
                  <FormControl>
                    {isFormLoading ? (
                      <Skeleton className="h-18 w-full rounded-md" />
                    ) : (
                      <Textarea
                        autoFocus
                        {...field}
                        readOnly={!canWrite}
                        className="max-h-24"
                        onChange={field.onChange}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="answer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('collections.add_faq.answer')}</FormLabel>
                  <FormControl>
                    {isFormLoading ? (
                      <div className="flex items-center gap-1 border-b p-1">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                      </div>
                    ) : (
                      // Fieldset is used to disable the editor toolbar
                      <fieldset disabled={!canWrite}>
                        <MarkdownEditorBlockNote
                          name="answer"
                          setValue={form.setValue}
                          defaultValue={field.value}
                          error={!!form.formState.errors.answer}
                          value={field.value}
                          readOnly={!canWrite}
                          key={editorKey}
                        />
                      </fieldset>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showKeepPublished && (
              <FormField
                control={form.control}
                name="keep_published"
                render={({ field }) => (
                  <FormItem className="flex gap-2 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!canWrite}
                      />
                    </FormControl>

                    <FormLabel className="flex flex-col items-start gap-1 leading-none">
                      {t('collections.add_faq.keep_published')}

                      <FormDescription>
                        {t('collections.add_faq.keep_published_description')}
                      </FormDescription>
                    </FormLabel>
                  </FormItem>
                )}
              />
            )}
          </SidebarWrapperContent>

          {canWrite && (
            <SidebarWrapperFooter>
              <Button aria-label="submit-faq-form" type="submit" disabled={isFormLoading}>
                {isEditing ? t('common.update') : t('common.create')}
              </Button>
              <Button type="button" variant="ghost-neutral" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
            </SidebarWrapperFooter>
          )}
        </SidebarWrapperForm>
      </Form>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            dispatch(setSidebarContent(null));
            resetReduxState();
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
